/**
 * Slaughter delivery widget on the farm dashboard: how many pigs a farm should have delivered each week,
 * derived from the piglets it registered 24 weeks earlier, against how many we hold slaughter data for.
 *
 * Mirrors `GET /dashboard/slaughter` (swagger:
 * `HealthSystem.API.Controllers.Dashboards.Responses.SlaughterForecastDashboardResponse`). Hand-written like
 * its siblings on the dashboard controller; field names match the API exactly so the two stay easy to diff.
 * Nothing here names the slaughter provider: which farms have a source is the backend's decision.
 *
 * Conventions follow the other dashboard responses: every `*Percent` is a whole percentage, and `null`
 * means "no value", never zero.
 */
export interface SlaughterForecastDashboard {
  /** False when the farm has no slaughter source at all. Everything else is then empty and the widget hides. */
  isEnabled: boolean;
  /**
   * False for a farm that buys its piglets in rather than farrowing them: it has no birth cohorts, so no
   * expectation. Deliveries are still reported, and the widget says why the expected side is missing.
   */
  hasBirthCohorts: boolean;
  /** Past weeks covered, current week included. Forecast weeks come on top of this. */
  weeksCount: number;
  /** Age at slaughter the expectation assumes. Backend configuration. */
  slaughterAgeWeeks: number;
  /** Loss across the whole fattening period the expectation assumes. Backend configuration. */
  expectedMortalityPercent: number;
  /** The current, still running week. */
  currentWeek: SlaughterForecastSummary;
  /**
   * The past weeks of the period, current week included, forecast weeks not. When some weeks have no
   * expectation (their cohort predates the farm's data), both sides are summed over the weeks that do -
   * otherwise their deliveries count against an expectation that never included them and a shortfall
   * reads as a surplus. Only a farm with no birth cohorts at all sums delivered over every week.
   */
  period: SlaughterForecastSummary;
  /** Pigs due across the forecast weeks. Null when there are none, or none of them has an expectation. */
  upcomingExpected: number | null;
  /** Oldest first: the past weeks, then the forecast weeks. No forecast weeks without birth cohorts. */
  weeks: SlaughterForecastWeek[];
}

export interface SlaughterForecastSummary {
  /** Null when no week in the range has an expectation. */
  expected: number | null;
  delivered: number;
  /** `delivered - expected`; negative is a shortfall. Null when `expected` is. */
  difference: number | null;
  /** `difference` as a percentage of `expected`. Null when there is nothing to divide by. */
  differencePercent: number | null;
}

export interface SlaughterForecastWeek {
  /** First day of the delivery week. Carried alongside the number because week numbers repeat across a year. */
  weekStartDate: string;
  year: number;
  weekNumber: number;
  /** A week after the current one: an expectation, no delivery yet. */
  isForecast: boolean;
  /**
   * Piglets registered in the birth week, less the expected mortality. Null when the birth week falls
   * before the farm's first registration - "not collected yet" rather than "registered nothing".
   */
  expected: number | null;
  /** Pigs slaughtered in this week. Null on a forecast week, zero on a past week with no deliveries. */
  delivered: number | null;
  /** The cohort this week's expectation comes from. */
  birthWeekStartDate: string;
  birthYear: number;
  birthWeekNumber: number;
  /** Piglets registered in the birth week. Null under the same rule as `expected`. */
  pigletsRegistered: number | null;
}

/** A delivered week is flagged when it lands under this share of its expectation. Same bar as the piglet chart. */
export const BELOW_EXPECTED_DELIVERY_THRESHOLD = 0.8;

export const isDeliveryBelowExpected = (week: SlaughterForecastWeek): boolean =>
  week.delivered !== null &&
  week.expected !== null &&
  week.expected > 0 &&
  week.delivered < week.expected * BELOW_EXPECTED_DELIVERY_THRESHOLD;
